import { useMemo, useState } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { BarChart } from "../components/BarChart";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { Insight } from "../components/Insight";
import { ProgressBar } from "../components/ProgressBar";
import { NavRow, RowGroup } from "../components/Rows";
import { Segmented } from "../components/Segmented";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { cycleOn, prePeriodDay } from "../domain/cycle";
import { addDays, dayLabel, daysBetween, dayMonth, parseDay, todayStr } from "../domain/date";
import type { Insight as InsightData, Window } from "../domain/insight";
import {
  LOW_STEPS,
  dailySteps,
  movementInsights,
  stepGoal,
  stepsWindow,
  vsPrevious,
  walksWindow,
  weekdayWeekend,
} from "../domain/movement";
import { hoursMinutes, signed, workSpan } from "../domain/progressView";
import { whoMinutes } from "../domain/training";
import { ewma, weightSeries } from "../domain/trend";
import { useKaya } from "../store/kayaStore";
import "./Progress.css";

type MoveWindow = Extract<Window, 28 | 90 | 0>;

const WINDOWS: { value: MoveWindow; label: string }[] = [
  { value: 28, label: "28 days" },
  { value: 90, label: "90 days" },
  { value: 0, label: "All" },
];

/** The chart is always the last 28 days: one bar a day stops being readable past that. */
const CHART_DAYS = 28;
/** A trend weight older than this is not the weight at the end of a later week. */
const TREND_STALE_DAYS = 7;

const n = (v: number | null) => (v === null ? "–" : v.toLocaleString());

/**
 * /progress/movement (ADR-033): steps and walks over 28 and 90 days. Today and the last seven
 * days belong to Today › Activity; this page links there instead of repeating them. Every
 * window ends yesterday — today's count is still climbing and would drag each average down.
 */
export default function ProgressMovement() {
  const S = useKaya((s) => s.S);
  const [w, setW] = useState<MoveWindow>(28);
  const today = todayStr();
  const end = addDays(today, -1);

  const trend = useMemo(() => ewma(weightSeries(S)), [S]);
  const weights = useMemo(
    () => (ds: string) => {
      let v: number | null = null;
      for (const p of trend) {
        if (p.date > ds) break;
        v = daysBetween(p.date, ds) <= TREND_STALE_DAYS ? p.v : null;
      }
      return v;
    },
    [trend]
  );

  const on = cycleOn(S);
  const ins = movementInsights(S, end, w, {
    prePeriod: (ds) => prePeriodDay(S, ds),
    cycleOn: on,
    weights,
  });
  const pick = (key: string): InsightData[] => ins.filter((x) => x.key === key);

  const sw = stepsWindow(S, end, w);
  const vs = vsPrevious(S, end, w);
  const ww = weekdayWeekend(S, end, w);
  const walks = walksWindow(S, end, w);
  const goal = stepGoal(S);
  const todaySteps = dailySteps(S, today);
  const who = whoMinutes(S, today);
  const work = workSpan(S, today, 28);

  const winWord = w === 0 ? "since you started" : `${w} days`;
  const tone = sw.phoneDays > 0 ? "phone" : sw.measuredDays > 0 ? "logged" : null;

  const chartDays = Array.from({ length: CHART_DAYS }, (_, i) => addDays(end, -(CHART_DAYS - 1) + i));
  // Mondays by date, the last bar by its day alone ("22"): a centred "21 Sep" on either edge bar
  // runs off the chart, so Mondays that close to an edge go unlabelled.
  const LAST = CHART_DAYS - 1;
  const bars = chartDays.map((ds, i) => {
    const d = parseDay(ds);
    const monday = d.getDay() === 1 && i >= 2 && i <= LAST - 3;
    const value = dailySteps(S, ds);
    return {
      key: ds,
      label: i === LAST ? String(d.getDate()) : monday ? dayMonth(ds) : "",
      name: dayLabel(ds),
      value,
      tone: value !== null && value < LOW_STEPS ? ("rest" as const) : ("brand" as const),
    };
  });
  const charted = bars.filter((b) => b.value !== null);

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title="Movement" back={{ to: "/progress", label: "Back to Progress" }} />
          <div className="pg">
            <Hero
              ariaLabel={w === 0 ? "Average steps since you started" : `Average steps, last ${w} days`}
              label={w === 0 ? "Steps · average since you started" : `Steps · ${w}-day average`}
              figure={sw.avg}
              unit="a day"
              line={`of ${goal.toLocaleString()} · ${sw.of ? `${sw.measuredDays} of ${sw.of} days measured` : "no full day yet"}${
                vs ? ` · ${signed(Math.round(vs.delta / 10) * 10)} on the previous ${w} days` : ""
              }`}
              sub={tone ? <Tag tone={tone} /> : undefined}
              visual={
                <ProgressBar
                  value={sw.avg}
                  max={Math.max(goal, sw.avg ?? 0)}
                  target={goal}
                  label={`${n(sw.avg)} a day`}
                  detail={`goal ${goal.toLocaleString()}`}
                  ariaLabel={`${n(sw.avg)} steps a day on average against a goal of ${goal.toLocaleString()}`}
                />
              }
            />

            <Segmented options={WINDOWS} value={w} onChange={setW} ariaLabel="Steps window" />

            <RowGroup heading="Daily steps · 28 days">
              <div className="pg-block">
                <BarChart
                  mode="single"
                  bars={bars}
                  rule={{ value: goal, label: "goal" }}
                  unit="steps"
                  readout={(b) => `${b.name} · ${n(b.value)} steps · of ${goal.toLocaleString()} goal`}
                  ariaLabel={
                    charted.length
                      ? `Steps a day, ${dayMonth(chartDays[0])} to ${dayMonth(end)}: ${charted.length} days measured`
                      : "No steps measured in the last 28 days"
                  }
                  empty="No steps measured in the last 28 days."
                />
              </div>
              {pick("move.avg").map((x) => (
                <Insight key={x.key} insight={x} />
              ))}
              <NavRow icon={I.list} title="Every day's steps" to="/progress/records/steps" />
            </RowGroup>

            <section className="k-group" aria-label="Weekday vs weekend">
              <h2 className="k-group__h">Weekday vs weekend</h2>
              <div className="k-rows">
                <div className="k-chapter__leads k-chapter__leads--split">
                  {(
                    [
                      [ww.weekday, "Weekdays"],
                      [ww.weekend, "Weekends"],
                    ] as const
                  ).map(([v, label]) => (
                    <div className="k-chapter__lead" key={label}>
                      <span className={`k-chapter__leadfig${v === null ? " is-empty" : ""}`}>{n(v)}</span>
                      <span className="k-chapter__leadlabel">{label} · a day</span>
                    </div>
                  ))}
                </div>
                {pick("move.weekend").map((x) => (
                  <Insight key={x.key} insight={x} />
                ))}
              </div>
            </section>

            <RowGroup heading="Days under 5,000">
              {pick("move.low").map((x) => (
                <Insight key={x.key} insight={x} first />
              ))}
            </RowGroup>

            <RowGroup heading={`Walks · ${winWord}`}>
              <NavRow kind="value" icon={I.walk} title="Walks" value={walks.count.toLocaleString()} />
              <NavRow
                kind="value"
                icon={I.tape}
                title="Distance"
                subtitle={walks.avgKm === null ? undefined : `avg ${walks.avgKm.toFixed(1)} km a walk`}
                value={
                  <>
                    {walks.km === null ? "–" : `${walks.km.toFixed(1)} km`}
                    {walks.km !== null && walks.cadenceWalks > 0 ? (
                      <>
                        {" "}
                        <Tag tone="phone" />
                      </>
                    ) : null}
                  </>
                }
              />
              <NavRow
                kind="value"
                icon={I.footsteps}
                title="Cadence"
                value={
                  walks.cadence === null ? (
                    "–"
                  ) : (
                    <>
                      ~{walks.cadence} steps/min <Tag tone="phone" />
                    </>
                  )
                }
              />
              {pick("move.walks").map((x) => (
                <Insight key={x.key} insight={x} />
              ))}
            </RowGroup>

            <RowGroup heading="Steps and the scale">
              {pick("move.lead").map((x) => (
                <Insight key={x.key} insight={x} first />
              ))}
            </RowGroup>

            <RowGroup>
              <NavRow
                icon={I.sun}
                title="Today"
                subtitle={`${n(todaySteps)} of ${goal.toLocaleString()} steps · by the hour`}
                to="/today/activity"
              />
            </RowGroup>

            <RowGroup heading="Connected">
              <NavRow
                icon={I.dumbbell}
                title="Training"
                subtitle={`${hoursMinutes(who.minutes)} active · last 7 days`}
                to="/progress/training"
              />
              <NavRow
                icon={I.flame}
                title="Energy"
                subtitle={work.avg === null ? "Work – kcal a day" : `Work ~${work.avg.toLocaleString()} kcal a day est.`}
                to="/progress/energy"
              />
            </RowGroup>
          </div>
        </Screen>
      </IonContent>
    </IonPage>
  );
}
